import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LuBoxes, LuLogOut, LuMenu, LuX } from 'react-icons/lu';
import { useAuth } from '../../context/AuthContext';
import { ROLES } from '../../theme';
import AbonnementBandeau from '../abonnement/AbonnementBandeau';
import './AppLayout.css';

/**
 * Coque commune à tous les profils : sidebar (menu selon le rôle) + contenu.
 *
 * - Menu par routes (admin, propriétaire) : rien à passer, l'élément actif
 *   est déduit de l'URL.
 * - Menu par onglets (gérant, caisse) : passer `activeId` et `onSelect`.
 * - `badges` : compteurs affichés à droite d'un élément, clés = path ou id.
 */
export default function AppLayout({ role, activeId, onSelect, subtitle, badges = {}, children }) {
  const { utilisateur, deconnecter } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [ouvert, setOuvert] = useState(false);
  const cfg = ROLES[role];
  const contenu = useRef(null);

  // Tableaux lisibles sur mobile : chaque cellule reçoit le titre de sa colonne
  // (data-label), le CSS transforme alors chaque ligne en fiche « libellé → valeur ».
  useEffect(() => {
    const racine = contenu.current;
    if (!racine) return undefined;
    let prevu = false;
    const etiqueter = () => {
      prevu = false;
      racine.querySelectorAll('table').forEach(table => {
        if (table.dataset.resp === 'non') return;
        const titres = [...table.querySelectorAll('thead th')].map(th => {
          const t = th.textContent.trim();
          return /^(photo|image|avatar)$/i.test(t) ? '' : t;
        });
        if (!titres.length) return;
        table.classList.add('sp-resp');
        table.querySelectorAll('tbody tr').forEach(tr => {
          let col = 0;
          [...tr.children].forEach(td => {
            const label = titres[col] ?? '';
            if (td.getAttribute('data-label') !== label) td.setAttribute('data-label', label);
            col += Number(td.getAttribute('colspan')) || 1;
          });
        });
      });
    };
    const planifier = () => { if (!prevu) { prevu = true; requestAnimationFrame(etiqueter); } };
    etiqueter();
    const obs = new MutationObserver(planifier);
    obs.observe(racine, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  const estActif = item => (item.id ? item.id === activeId : item.path === pathname);

  const handleClick = item => {
    setOuvert(false);
    if (item.id) onSelect?.(item.id);
    else navigate(item.path);
  };

  const handleDeconnexion = () => {
    deconnecter();
    navigate('/login');
  };

  const initiales = `${utilisateur?.prenom?.[0] || ''}${utilisateur?.nom?.[0] || ''}`.toUpperCase();

  return (
    <div className={`sp-shell${ouvert ? ' is-open' : ''}`} style={{ '--accent': cfg.accent, '--sidebar': cfg.sidebar }}>
      <aside className="sp-sidebar" aria-label="Navigation principale">
        <div className="sp-brand">
          <div className="sp-brand-mark"><LuBoxes aria-hidden /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sp-brand-name">StockPro</div>
            <div className="sp-brand-role">{subtitle || `Espace ${cfg.label.toLowerCase()}`}</div>
          </div>
        </div>

        <nav className="sp-nav">
          <div className="sp-nav-label">Menu</div>
          {cfg.nav.map(item => {
            const Icon = item.icon;
            const badge = badges[item.id || item.path];
            return (
              <button
                key={item.id || item.path}
                className="sp-nav-item"
                aria-current={estActif(item) ? 'page' : undefined}
                onClick={() => handleClick(item)}
              >
                <Icon aria-hidden />
                <span style={{ flex: 1 }}>{item.label}</span>
                {badge > 0 && <span className="sp-nav-badge" aria-label={`${badge} alerte(s)`}>{badge}</span>}
              </button>
            );
          })}
        </nav>

        <div className="sp-user">
          <div className="sp-user-row">
            <div className="sp-avatar" aria-hidden>{initiales || '?'}</div>
            <div style={{ minWidth: 0 }}>
              <div className="sp-user-name">{utilisateur?.prenom} {utilisateur?.nom}</div>
              <div className="sp-user-meta">{cfg.label}</div>
            </div>
          </div>
          <button className="sp-logout" onClick={handleDeconnexion}>
            <LuLogOut aria-hidden /> Déconnexion
          </button>
        </div>
      </aside>

      <div className="sp-backdrop" onClick={() => setOuvert(false)} />

      <div className="sp-main">
        <header className="sp-topbar">
          <button
            className="sp-menu-btn"
            onClick={() => setOuvert(o => !o)}
            aria-label={ouvert ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={ouvert}
          >
            {ouvert ? <LuX /> : <LuMenu />}
          </button>
          <span style={{ fontWeight: 700 }}>StockPro</span>
        </header>
        <AbonnementBandeau role={role} />
        <div className="sp-main-inner" ref={contenu}>{children}</div>
      </div>
    </div>
  );
}
